import React from 'react';
import { motion } from 'framer-motion';
import { BsArrowDownRight, BsDownload, BsFileEarmarkPdf, BsFileEarmarkSpreadsheet } from 'react-icons/bs';

interface FinancialsTabProps {
    financials: any[];
}

const FinancialsTab: React.FC<FinancialsTabProps> = ({ financials = [] }) => {

    const calculateSpendData = () => {
        const months = [];
        const spend = [0, 0, 0, 0, 0, 0];
        const now = new Date();
        for (let i = 5; i >= 0; i--) {
            const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
            months.push(d.toLocaleString('default', { month: 'short' }));
        }

        financials.forEach(f => {
            if (f.status === 'Paid') {
                const date = new Date(f.date);
                const monthDiff = (now.getFullYear() - date.getFullYear()) * 12 + (now.getMonth() - date.getMonth());
                if (monthDiff >= 0 && monthDiff < 6) {
                    spend[5 - monthDiff] += Number(f.amount) || 0;
                }
            }
        });

        return { months, spend };
    };

    const { months: actualMonths, spend: actualSpend } = calculateSpendData();
    const currentSpend = actualSpend[5];
    const prevSpend = actualSpend[4];
    const spendDiffPercent = prevSpend === 0 ? 0 : Math.round(((currentSpend - prevSpend) / prevSpend) * 100);
    const projectedSpend = currentSpend > 0 ? Math.round(currentSpend * 1.1) : 0; // simplistic projection

    const maxSpend = Math.max(...actualSpend, projectedSpend, 1000); // minimum 1000 to keep chart scale

    const totalPaid = financials.filter(f => f.status === 'Paid').reduce((sum, f) => sum + (Number(f.amount) || 0), 0);
    const platformFees = Math.round(totalPaid * 0.05); // Simulated 5% fee

    return (
        <div className="space-y-8">
            {/* Top Financial Dashboard Section */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

                {/* Spend Graph (Simulated Bar Chart) */}
                <div className="lg:col-span-2 bg-white/5 border border-white/10 rounded-xl p-6 relative">
                    <div className="flex justify-between items-center mb-8">
                        <div>
                            <h3 className="text-xl font-bold text-white mb-1">Monthly Spend</h3>
                            <p className="text-xs text-white/50">Your spending velocity over the last 6 months</p>
                        </div>
                        <div className="text-right">
                            <div className="text-2xl font-serif font-bold text-premium-gold">${currentSpend.toLocaleString()}</div>
                            {spendDiffPercent !== 0 && (
                                <div className={`text-xs flex items-center justify-end gap-1 ${spendDiffPercent > 0 ? 'text-red-400' : 'text-green-400'}`}>
                                    <BsArrowDownRight className={spendDiffPercent > 0 ? 'rotate-180' : ''} /> {Math.abs(spendDiffPercent)}% from {actualMonths[4]}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Simple CSS Bar Chart */}
                    <div className="h-48 flex items-end gap-4 justify-between mt-4">
                        {actualMonths.map((month, idx) => (
                            <div key={month} className="flex flex-col items-center flex-1 group">
                                <div className="w-full relative flex justify-center items-end h-40">
                                    <motion.div
                                        initial={{ height: 0 }}
                                        animate={{ height: `${(actualSpend[idx] / maxSpend) * 100}%` }}
                                        transition={{ duration: 1, delay: idx * 0.1 }}
                                        className="w-12 bg-premium-gold/80 rounded-t-sm relative group-hover:bg-premium-gold transition-colors"
                                    >
                                        <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-black text-white text-[10px] py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                                            ${actualSpend[idx].toLocaleString()}
                                        </div>
                                    </motion.div>
                                </div>
                                <span className="text-xs text-white/50 mt-2">{month}</span>
                            </div>
                        ))}
                        {/* Projected Month */}
                        <div className="flex flex-col items-center flex-1 group">
                            <div className="w-full relative flex justify-center items-end h-40">
                                <motion.div
                                    initial={{ height: 0 }}
                                    animate={{ height: `${(projectedSpend / maxSpend) * 100}%` }}
                                    transition={{ duration: 1, delay: 0.6 }}
                                    className="w-12 bg-white/20 border border-dashed border-white/40 rounded-t-sm relative group-hover:bg-white/40 transition-colors"
                                >
                                    <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-black text-white text-[10px] py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                                        ~${projectedSpend.toLocaleString()}
                                    </div>
                                </motion.div>
                            </div>
                            <span className="text-xs text-premium-gold font-bold mt-2">Next (Est)</span>
                        </div>
                    </div>
                </div>

                {/* Quick Actions & Breakdown */}
                <div className="flex flex-col gap-6">
                    <div className="bg-white/5 border border-white/10 rounded-xl p-6 flex-1">
                        <h3 className="text-white/60 text-sm font-medium uppercase tracking-wider mb-4">Export & Taxes</h3>
                        <div className="space-y-3">
                            <button className="w-full flex items-center justify-between p-3 bg-black/20 hover:bg-white/10 border border-white/5 rounded-lg transition-colors group">
                                <div className="flex items-center gap-3">
                                    <BsFileEarmarkPdf className="text-red-400 text-lg" />
                                    <span className="text-sm font-medium text-white group-hover:text-premium-gold transition-colors">{new Date().getFullYear()} Tax Summary</span>
                                </div>
                                <BsDownload className="text-white/40" />
                            </button>
                            <button className="w-full flex items-center justify-between p-3 bg-black/20 hover:bg-white/10 border border-white/5 rounded-lg transition-colors group">
                                <div className="flex items-center gap-3">
                                    <BsFileEarmarkSpreadsheet className="text-green-400 text-lg" />
                                    <span className="text-sm font-medium text-white group-hover:text-premium-gold transition-colors">All Transactions CSV</span>
                                </div>
                                <BsDownload className="text-white/40" />
                            </button>
                        </div>

                        <div className="mt-6 pt-6 border-t border-white/10">
                            <div className="flex justify-between items-center mb-2 text-sm">
                                <span className="text-white/60">Platform Fees (YTD)</span>
                                <span className="text-white font-bold">${platformFees.toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between items-center text-sm">
                                <span className="text-white/60">Taxes Withheld</span>
                                <span className="text-white font-bold">$0</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Invoices Table */}
            <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
                <div className="p-6 border-b border-white/10 flex justify-between items-center">
                    <h3 className="text-lg font-bold text-white">Recent Invoices</h3>
                    <button className="text-sm text-premium-gold hover:underline">View All</button>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead className="bg-black/20 text-white/50 text-xs uppercase tracking-wider">
                            <tr>
                                <th className="p-4 pl-6 font-medium">Invoice ID</th>
                                <th className="p-4 font-medium">Project</th>
                                <th className="p-4 font-medium">Date Issued</th>
                                <th className="p-4 font-medium">Status</th>
                                <th className="p-4 text-right pr-6 font-medium">Amount</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-white/5">
                            {financials.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="p-8 text-center text-white/50 italic py-8 border border-white/5 bg-black/20 rounded-lg">
                                        No financial transactions found. Start working on projects to generate invoices.
                                    </td>
                                </tr>
                            ) : (
                                financials.map((f, i) => (
                                    <tr key={f.id || i} className="text-sm hover:bg-white/5 transition-colors group">
                                        <td className="p-4 pl-6 text-white font-medium cursor-pointer group-hover:text-premium-gold">
                                            {f.id ? String(f.id).substring(0, 8).toUpperCase() : `INV-${i + 1}`}
                                        </td>
                                        <td className="p-4 text-white/80">{f.project || 'Project Milestones'}</td>
                                        <td className="p-4 text-white/50">{new Date(f.date).toLocaleDateString()}</td>
                                        <td className="p-4">
                                            <span className={`px-2 py-1 rounded-full text-xs font-bold ${f.status === 'Paid' ? 'bg-green-500/10 text-green-400 border border-green-500/20' : 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/20'}`}>
                                                {f.status}
                                            </span>
                                        </td>
                                        <td className="p-4 pr-6 text-right font-bold text-white group-hover:text-premium-gold transition-colors">
                                            ${f.amount?.toLocaleString() || 0}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default FinancialsTab;
